import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Grid,
  GridItem,
  SimpleGrid,
  useTheme,
  Image,
  VStack,
  Divider,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  useToast,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  AspectRatio,
  Link,
  Flex,
} from "@chakra-ui/react";
import SlideShow from "./SlideShow";
import ServiceBox from "./ServiceBox";
import NewsBox from "./NewsBox";
import ActivityBox from "./ActivityBox";
import * as IconButton from "../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullhorn,
  faClipboardQuestion,
  faCommentDots,
  faCow,
  faFileCircleQuestion,
  faFilePdf,
  faFilm,
  faHouseChimney,
  faPersonCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import MapInfoBox from "./MapinfoBox";
import { useAuth } from "../context/AuthContext";
import { newsItems } from "./data/newsItems";
import { activityItems } from "./data/activityItems";
import { epidemic } from "./data/epidemic";
import { questionnaire } from "./data/questionnaire";
import { questionnairestaff } from "./data/questionnairestaff";
import { goathouse } from "./data/goathouse";
import { announcement } from "./data/announcement";
import { vaxhubVideoTutorial } from "./data/vaxhubVideoTutorial";
import VideoWithPoster from "./VideoWithPoster";
const HomePage = () => {
  const theme = useTheme();
  const toast = useToast();
  const { sessionId } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Responsive Sizes
  const bannerImageHeight = "clamp(200px, 40vh, 500px)";
  const sectionTitleFontSize = "clamp(1.0rem, 4vw, 1.3rem)";
  const subSectionTitleFontSize = "clamp(0.8rem, 2.2vw, 1rem)";
  const bodyTextFontSize = "clamp(0.6rem, 2.5vw, 0.8rem)";
  const smallTextFontSize = "clamp(0.7rem, 2vw, 0.8rem)";
  const iconBoxSize = "clamp(2.5rem, 6vw, 3.5rem)";
  const infoBoxHeaderFontSize = "clamp(0.9rem, 2.2vw, 1.2rem)";
  const infoBoxSubHeaderFontSize = "clamp(0.6rem, 2.2vw, 0.8rem)";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    VisitorName: "",
    VisitorTel: "",
    VisitorEmail: "",
    CommentText: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Fetch notifications from getNotificationList API
  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getNotificationList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setNotifications(response.notifications || []);
      } else {
        console.error("Failed to fetch notifications:", response.error);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchNotifications();
    }
  }, [sessionId]);

  const handleComment = () => {
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.VisitorName.trim()) {
      newErrors.VisitorName = "กรุณากรอกชื่อ";
    }
    if (!formData.CommentText.trim()) {
      newErrors.CommentText = "กรุณากรอกความคิดเห็น";
    }
    if (
      formData.VisitorEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.VisitorEmail)
    ) {
      newErrors.VisitorEmail = "รูปแบบอีเมลไม่ถูกต้อง";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await apiService({
        method: "POST",
        url: "/createVisitorComments",
        data: formData,
      });

      if (response.result === "success") {
        toast({
          title: "ส่งความคิดเห็นสำเร็จ",
          description: "ขอบคุณสำหรับความคิดเห็นของคุณ",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setIsModalOpen(false);
        setFormData({
          VisitorName: "",
          VisitorTel: "",
          VisitorEmail: "",
          CommentText: "",
        });
      } else {
        const errorMessage = response.message || "ไม่สามารถส่งความคิดเห็นได้";
        setSubmitError(errorMessage);
        toast({
          title: "เกิดข้อผิดพลาด",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "เกิดข้อผิดพลาดในการส่งความคิดเห็น";
      setSubmitError(errorMessage);
      toast({
        title: "เกิดข้อผิดพลาด",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <Box
        maxW="container.2xl"
        mx="auto"
        mt={5}
        px={{ base: 2, md: 4, lg: 6 }}
        py={1}
        bg="white"
        borderRadius="2xl"
      >
        {/* Notification Section */}
      {notifications.length > 0 && (
        <Box p={4} borderRadius="md" boxShadow="md" mb={5}>
          <Heading size="md" mb={4} color="gray.600">
            การแจ้งเตือน
          </Heading>
          <VStack spacing={3} align="stretch">
            {notifications.map((notification, index) => (
              <Alert key={index} status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{notification.message}</AlertDescription>
              </Alert>
            ))}
          </VStack>
        </Box>
      )}

      <Grid
        templateColumns={{ base: "1fr", xl: "1fr 1fr" }}
        gap={{ base: 6, md: 8, xl: 10 }}
      >
        {/* --- Left Column --- */}
        <GridItem colSpan={1}>
          {/* Banner */}
          <Box w="100%" borderRadius="lg" overflow="hidden">
            <Image
              width="100%"
              height="auto"
              src="/banner/bpr01.png"
              alt="Banner 1"
            />
            {/* <SlideShow /> */}
          </Box>

          <ServiceBox />

          {/* Vaxhub VDO Section */}
          <Box
            my={{ base: 6, md: 8 }}
            w="full"
            bg="#eef4fb"
            borderRadius="2xl"
            p={{ base: 4, md: 6, lg: 8 }}
            boxShadow="sm"
          >
            <HStack mb={{ base: 4, md: 6 }} align="flex-start" spacing={4}>
              <Flex
                flexShrink={0}
                boxSize={{ base: "2.5rem", md: "3rem" }}
                bg="blue.100"
                borderRadius="lg"
                align="center"
                justify="center"
              >
                <FontAwesomeIcon
                  icon={faFilm}
                  style={{ fontSize: "1.25rem" }}
                  color={theme.colors.blue[700]}
                />
              </Flex>
              <VStack align="start" spacing={1}>
                <Heading
                  fontSize={sectionTitleFontSize}
                  color="blue.800"
                  lineHeight="shorter"
                >
                  {vaxhubVideoTutorial.title}
                </Heading>
                <Text fontSize={bodyTextFontSize} color="blue.600">
                  {vaxhubVideoTutorial.subtitle}
                </Text>
              </VStack>
            </HStack>

            <Grid
              templateColumns={{ base: "1fr", lg: "3fr 2fr" }}
              gap={{ base: 5, md: 8 }}
              alignItems="stretch"
            >
              <Box
                border="4px solid"
                borderColor="blue.700"
                borderRadius="xl"
                overflow="hidden"
                bg="black"
              >
                <AspectRatio ratio={16 / 9}>
                  <VideoWithPoster
                    src={vaxhubVideoTutorial.videoSrc}
                    poster={vaxhubVideoTutorial.poster}
                    posterTime={vaxhubVideoTutorial.posterTime}
                    title={vaxhubVideoTutorial.title}
                  />
                </AspectRatio>
              </Box>

              <Flex direction="column" justify="space-between" gap={5}>
                <Box>
                  <Heading
                    fontSize={subSectionTitleFontSize}
                    color="blue.800"
                    mb={{ base: 3, md: 4 }}
                  >
                    {vaxhubVideoTutorial.stepsTitle}
                  </Heading>
                  <VStack align="stretch" spacing={3}>
                    {vaxhubVideoTutorial.steps.map((step, index) => (
                      <HStack key={index} align="flex-start" spacing={3}>
                        <Flex
                          flexShrink={0}
                          boxSize={{ base: 7, md: 8 }}
                          bg="blue.700"
                          color="white"
                          borderRadius="full"
                          align="center"
                          justify="center"
                          fontSize={smallTextFontSize}
                          fontWeight="bold"
                        >
                          {index + 1}
                        </Flex>
                        <Text
                          fontSize={bodyTextFontSize}
                          color="blue.900"
                          lineHeight="tall"
                          pt={0.5}
                        >
                          {step}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </Box>

              </Flex>
            </Grid>
          </Box>

          {/* Questionnaire Section */}
          <Box my={{ base: 6, md: 8 }} w="full">
            {/* เปลี่ยนจาก SimpleGrid เป็น Grid */}
            <Grid
              // กำหนดให้มี 2 คอลัมน์ อัตราส่วน 2:1 ในจอใหญ่ (lg) และ 1 คอลัมน์ในจอเล็ก (base)
              templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
              gap={{ base: 5, md: 6 }} // กำหนดระยะห่างระหว่างกล่อง
            >

          {/* Questionnaire FarmerSection */}
          <Box
            my={{ base: 6, md: 8 }}
            py={{ base: 3, md: 5 }}
            px={{ base: 2, md: 3 }}
          >
            <Heading
              fontSize={sectionTitleFontSize}
              mb={{ base: 3, md: 4 }}
              color="gray.600"
            >
              แบบสอบถาม - สำหรับเกษตรกร และประชาชนทั่วไป
            </Heading>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {questionnaire.slice(0, 4).map((item, index) => (
                <Link
                  key={`questionnaire-${index}`}
                  href={item.url}
                  isExternal
                  _hover={{ textDecoration: "none" }}
                >
                  <Box
                    bgColor="blue.50"
                    p={{ base: 3, md: 4 }}
                    borderRadius="lg"
                    cursor="pointer"
                    transition="box-shadow 0.2s, transform 0.2s"
                    _hover={{
                      boxShadow: "md",
                      transform: "translateY(-1px)",
                    }}
                  >
                    <HStack spacing={3} alignItems="center">
                      <FontAwesomeIcon
                        icon={faClipboardQuestion}
                        style={{ fontSize: "1rem" }}
                        color={theme.colors.blue[500]}
                      />
                      <Text
                        fontSize={smallTextFontSize}
                        color="blue.700"
                        fontWeight="medium"
                      >
                        {item.date}
                      </Text>
                    </HStack>
                    <Text fontSize={bodyTextFontSize} mt={2} color="gray.600">
                      {item.title}
                    </Text>
                    <Text
                      fontSize={smallTextFontSize}
                      mt={2}
                      color="blue.600"
                      fontWeight="semibold"
                    >
                      เปิดแบบสอบถาม →
                    </Text>
                  </Box>
                </Link>
              ))}
            </VStack>
          </Box>

          {/* Questionnaire Staff Section */}
          <Box
            my={{ base: 6, md: 8 }}
            py={{ base: 3, md: 5 }}
            px={{ base: 2, md: 3 }}
          >
            <Heading
              fontSize={sectionTitleFontSize}
              mb={{ base: 3, md: 4 }}
              color="gray.600"
            >
              แบบสอบถาม - สำหรับเจ้าหน้าที่กรมปศุสัตว์
            </Heading>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {questionnairestaff.slice(0, 4).map((item, index) => (
                <Link
                  key={`questionnaire-${index}`}
                  href={item.url}
                  isExternal
                  _hover={{ textDecoration: "none" }}
                >
                  <Box
                    bgColor="red.50"
                    p={{ base: 3, md: 4 }}
                    borderRadius="lg"
                    cursor="pointer"
                    transition="box-shadow 0.2s, transform 0.2s"
                    _hover={{
                      boxShadow: "md",
                      transform: "translateY(-1px)",
                    }}
                  >
                    <HStack spacing={3} alignItems="center">
                      <FontAwesomeIcon
                        icon={faClipboardQuestion}
                        style={{ fontSize: "1rem" }}
                        color={theme.colors.blue[500]}
                      />
                      <Text
                        fontSize={smallTextFontSize}
                        color="blue.700"
                        fontWeight="medium"
                      >
                        {item.date}
                      </Text>
                    </HStack>
                    <Text fontSize={bodyTextFontSize} mt={2} color="gray.600">
                      {item.title}
                    </Text>
                    <Text
                      fontSize={smallTextFontSize}
                      mt={2}
                      color="blue.600"
                      fontWeight="semibold"
                    >
                      เปิดแบบสอบถาม →
                    </Text>
                  </Box>
                </Link>
              ))}
            </VStack>
          </Box>

            </Grid>
          </Box>


          {/* News Section */}
          {/*
          <Box
            my={{ base: 6, md: 8 }}
            py={{ base: 3, md: 5 }}
            px={{ base: 2, md: 3 }}
          >
            <Heading
              fontSize={sectionTitleFontSize}
              mb={{ base: 3, md: 4 }}
              color="gray.500"
            >
              ข่าวประชาสัมพันธ์
            </Heading>
            <Divider mb={{ base: 4, md: 5 }} />
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={{ base: 5, md: 6 }}
            >
              {newsItems.map((item, index) => (
                <NewsBox key={index} {...item} direction="vertical" />
              ))}
            </SimpleGrid>
            <Divider mt={{ base: 4, md: 5 }} />
          </Box>
  */}
          {/* Activity Section */}
          {/*
          <Box
            my={{ base: 6, md: 8 }}
            py={{ base: 3, md: 5 }}
            px={{ base: 2, md: 3 }}
          >
            <Heading
              fontSize={sectionTitleFontSize}
              mb={{ base: 3, md: 4 }}
              color="gray.600"
            >
              กิจกรรม
            </Heading>
            <Divider mb={{ base: 4, md: 5 }} />
            <VStack spacing={{ base: 5, md: 6 }} align="stretch">
              {activityItems.map((item, index) => (
                <NewsBox key={index} {...item} direction="horizontal" />
              ))}
            </VStack>
            <Divider mt={{ base: 4, md: 5 }} />
          </Box>
          */}
        </GridItem>

        {/* --- Right Column --- */}
        <GridItem colSpan={1}>
          {/* Map Section */}
          <Box mb={{ base: 6, md: 8 }} w="full" borderRadius="xl">
            <Box
              minH={{ base: "350px", md: "450px", lg: "550px" }}
              borderRadius="xl"
              overflow="hidden"
            >
              <MapInfoBox />
            </Box>
          </Box>

          {/* Info Boxes (Video, FMD, Comment) */}
          <Box my={{ base: 6, md: 8 }} w="full">
            {/* เปลี่ยนจาก SimpleGrid เป็น Grid */}
            <Grid
              // กำหนดให้มี 2 คอลัมน์ อัตราส่วน 2:1 ในจอใหญ่ (lg) และ 1 คอลัมน์ในจอเล็ก (base)
              templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
              gap={{ base: 5, md: 6 }} // กำหนดระยะห่างระหว่างกล่อง
            >
              {/* Video Box (ใช้พื้นที่ 2 ส่วน) */}
              <Box w="full" borderRadius="xl" overflow="hidden" boxShadow="lg">
                <Box position="relative" height="0" paddingBottom="56.25%">
                  <Box
                    as="iframe"
                    src="https://www.youtube.com/embed/Ct3jaPDPcbs?si=L_ozKpeDbMLYvGuS" // ใส่ลิงก์วิดีโอของคุณ
                    title="YouTube video player"
                    width="100%"
                    height="100%"
                    position="absolute"
                    top="0"
                    left="0"
                    allowFullScreen
                  />
                </Box>
              </Box>

              {/* Comment Box (ใช้พื้นที่ 1 ส่วน) */}
              <Box p={5} w="full" bg="teal.400" borderRadius="xl">
                <VStack
                  spacing={4}
                  h="100%"
                  alignItems="start"
                  justifyContent="space-between"
                >
                  <VStack alignItems="start">
                    <Box>
                      <FontAwesomeIcon
                        icon={faCommentDots}
                        style={{ fontSize: iconBoxSize }}
                        color="white"
                      />
                    </Box>
                    <VStack alignItems="start" spacing={2}>
                      <Heading
                        fontSize={subSectionTitleFontSize}
                        fontWeight="normal"
                        color="white"
                      >
                        แสดงความคิดเห็น
                      </Heading>
                      <Text
                        fontSize={bodyTextFontSize}
                        fontWeight="normal"
                        color="white"
                        mb={2}
                      >
                        ส่งความคิดเห็น หรือ ข้อเสนอแนะได้ที่นี่
                      </Text>
                    </VStack>
                  </VStack>
                  <IconButton.Send
                    onClick={handleComment}
                    size="sm"
                    fontSize="xs"
                    label="ส่งความคิดเห็น"
                    width="auto"
                    px={4}
                  />
                </VStack>
              </Box>

              {/* นำ Box ของ "โรคปากเท้าเปื่อยฯ" ออกไป */}
            </Grid>
          </Box>


          {/* Goat House Section */}

          <Box my={{ base: 6, md: 8 }} w="full">
            {/* เปลี่ยนจาก SimpleGrid เป็น Grid */}
            <Grid
              // กำหนดให้มี 2 คอลัมน์ อัตราส่วน 2:1 ในจอใหญ่ (lg) และ 1 คอลัมน์ในจอเล็ก (base)
              templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
              gap={{ base: 5, md: 6 }} // กำหนดระยะห่างระหว่างกล่อง
            >
              {/* Video Box (ใช้พื้นที่ 2 ส่วน) */}
              {/* Video Box (ใช้พื้นที่ 2 ส่วน) */}
              <Box w="full" borderRadius="xl" overflow="hidden" boxShadow="lg">
                <Box position="relative" height="0" paddingBottom="56.25%">
                  <Box
                    as="iframe"
                    src="https://vaxhub.dld.go.th/videos/goat.mp4" // ใส่ลิงก์วิดีโอของคุณ
                    title="YouTube video player"
                    width="100%"
                    height="100%"
                    position="absolute"
                    top="0"
                    left="0"
                    allowFullScreen
                  />
                </Box>
              </Box>

              {/* Detail Box (ใช้พื้นที่ 1 ส่วน) */}
              <Box p={5} w="full" bg="gray.400" borderRadius="xl">
                <VStack
                  spacing={4}
                  h="100%"
                  alignItems="start"
                  justifyContent="space-between"
                >
                  <VStack alignItems="start">
                    <Box>
                    <Heading
                        fontSize={subSectionTitleFontSize}
                        fontWeight="normal"
                        color="white"
                      >
                        รูปแบบโรงเรือนแพะเนื้อมาตรฐานสำหรับประเทศไทย
                    </Heading>
                    </Box>

                  </VStack>
                  <VStack spacing={{ base: 3, md: 4 }} align="stretch" w="100%">
                    {goathouse.slice(0, 4).map((item, index) => (
                      <Link
                        key={`questionnaire-${index}`}
                        href={item.url}
                        isExternal
                        _hover={{ textDecoration: "none" }}
                      >
                        <Box
                          bgColor="orange.50"
                          p={{ base: 3, md: 4 }}
                          borderRadius="lg"
                          cursor="pointer"
                          transition="box-shadow 0.2s, transform 0.2s"
                          _hover={{
                            boxShadow: "md",
                            transform: "translateY(-1px)",
                          }}
                        >
                          <HStack spacing={3} alignItems="center">
                            <FontAwesomeIcon
                              icon={faHouseChimney}
                              style={{ fontSize: "1rem" }}
                              color={theme.colors.orange[500]}
                            />
                            <Text
                              fontSize={smallTextFontSize}
                              color="orange.700"
                              fontWeight="medium"
                            >
                              {item.date}
                            </Text>
                          </HStack>
                          <Text fontSize={bodyTextFontSize} mt={2} color="gray.600">
                            {item.title}
                          </Text>
                          <Text
                            fontSize={smallTextFontSize}
                            mt={2}
                            color="orange.600"
                            fontWeight="semibold"
                          >
                            ดาวน์โหลดไฟล์ <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: "1rem" }}  />
                          </Text>
                        </Box>
                      </Link>
                    ))}
            </VStack>
                </VStack>
              </Box>

              {/* นำ Box ของ "โรคปากเท้าเปื่อยฯ" ออกไป */}
            </Grid>
          </Box>



          {/* Announcement Sections */}
          {/*
          <Box my={{ base: 6, md: 8 }} w="full">
            <Heading
              fontSize={sectionTitleFontSize}
              mb={{ base: 3, md: 4 }}
              color="gray.600"
            >
              ประกาศโรคระบาด
            </Heading>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {epidemic.slice(0, 4).map((item, index) => (
                <Box
                  key={`disease-${index}`}
                  bgColor="blue.50"
                  p={{ base: 3, md: 4 }}
                  borderRadius="lg"
                >
                  <HStack spacing={3} alignItems="center">
                    <FontAwesomeIcon
                      icon={faBullhorn}
                      style={{ fontSize: "1rem" }}
                      color={theme.colors.blue[500]}
                    />
                    <Text
                      fontSize={smallTextFontSize}
                      color="blue.700"
                      fontWeight="medium"
                    >
                      {item.date}
                    </Text>
                  </HStack>
                  <Text fontSize={bodyTextFontSize} mt={2} color="gray.600">
                    {item.title}
                  </Text>
                </Box>
              ))}
            </VStack>

            <Heading
              fontSize={sectionTitleFontSize}
              mt={{ base: 6, md: 8 }}
              mb={{ base: 3, md: 4 }}
              color="gray.600"
            >
              ประกาศกรมปศุสัตว์
            </Heading>
            <VStack spacing={{ base: 3, md: 4 }} align="stretch">
              {announcement.slice(0, 6).map((item, index) => (
                <Box
                  key={`dld-${index}`}
                  bgColor="pink.50"
                  p={{ base: 3, md: 4 }}
                  borderRadius="lg"
                >
                  <HStack spacing={3} alignItems="center">
                    <FontAwesomeIcon
                      icon={faBullhorn}
                      style={{ fontSize: "1rem" }}
                      color={theme.colors.pink[500]}
                    />
                    <Text
                      fontSize={smallTextFontSize}
                      color="pink.700"
                      fontWeight="medium"
                    >
                      {item.date}
                    </Text>
                  </HStack>
                  <Text fontSize={bodyTextFontSize} mt={2} color="gray.700">
                    {item.title}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Box>
          */}
        </GridItem>
      </Grid>

      
          {/* Questionnaire Section */}
          <Box my={{ base: 6, md: 8 }} w="full">
            {/* เปลี่ยนจาก SimpleGrid เป็น Grid */}
            <Grid
              // กำหนดให้มี 2 คอลัมน์ อัตราส่วน 2:1 ในจอใหญ่ (lg) และ 1 คอลัมน์ในจอเล็ก (base)
              templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
              gap={{ base: 5, md: 6 }} // กำหนดระยะห่างระหว่างกล่อง
            >

            {/* Banner */}
            <Box w="100%" borderRadius="lg" overflow="hidden">
              <a href="https://dcontrol.dld.go.th" target="_blank">
                <Image
                  width="100%"
                  height="auto"
                  src="/banner/DLD_DControl.png"
                  alt="Banner 1"
                />
              </a>
              {/* <SlideShow /> */}
            </Box>

          {/* Banner */}
          <Box w="100%" borderRadius="lg" overflow="hidden">
            <a href="https://certify.dld.go.th" target="_blank">
                <Image
                  width="100%"
                  height="auto"
                  src="/banner/DLD_Certify.png"
                  alt="Banner 1"
                />
              </a>
            {/* <SlideShow /> */}
          </Box>
          </Grid>
          </Box>

      {/* Comment Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size={{ base: "full", sm: "md" }}
      >
        <ModalOverlay />
        <ModalContent mx={{ base: 2 }}>
          <ModalHeader>ส่งความคิดเห็น</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isInvalid={!!errors.VisitorName} mb={4} isRequired>
              <FormLabel>ชื่อ</FormLabel>
              <Input
                name="VisitorName"
                value={formData.VisitorName}
                onChange={handleInputChange}
                placeholder="กรอกชื่อ"
              />
              {errors.VisitorName && (
                <FormErrorMessage>{errors.VisitorName}</FormErrorMessage>
              )}
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>หมายเลขโทรศัพท์</FormLabel>
              <Input
                name="VisitorTel"
                value={formData.VisitorTel}
                onChange={handleInputChange}
                placeholder="กรอกหมายเลขโทรศัพท์ (ถ้ามี)"
                type="tel"
              />
            </FormControl>
            <FormControl isInvalid={!!errors.VisitorEmail} mb={4}>
              <FormLabel>อีเมล</FormLabel>
              <Input
                name="VisitorEmail"
                value={formData.VisitorEmail}
                onChange={handleInputChange}
                placeholder="กรอกอีเมล (ถ้ามี)"
                type="email"
              />
              {errors.VisitorEmail && (
                <FormErrorMessage>{errors.VisitorEmail}</FormErrorMessage>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.CommentText} mb={4} isRequired>
              <FormLabel>ความคิดเห็น</FormLabel>
              <Textarea
                name="CommentText"
                value={formData.CommentText}
                onChange={handleInputChange}
                placeholder="กรอกความคิดเห็น"
                rows={5}
              />
              {errors.CommentText && (
                <FormErrorMessage>{errors.CommentText}</FormErrorMessage>
              )}
            </FormControl>
            {submitError && (
              <Text color="red.500" mb={4} fontSize="sm">
                {submitError}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              ส่ง
            </Button>
            <Button
              variant="ghost"
              onClick={() => setIsModalOpen(false)}
              isDisabled={isSubmitting}
            >
              ยกเลิก
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default HomePage;